import { useEffect, useRef, useState } from "react";

import { trackNeedsStart, trackNeedsStep, trackNeedsComplete } from "@/utils/analytics";

import NeedsAnalysisSteps from "./NeedsAnalysisSteps";
import { QuestionnaireStep, QuestionnaireStepOption } from "../../schema/questionnaire";
import { QuestionnaireImportance } from "../../enum/QuestionnaireImportance.enum";
import NeedsAnalysisFinalResults from "./NeedsAnalysisFinalResults";

interface NeedsAnalysisFlowProps {
	filteredNeedsAnalysis: QuestionnaireStep[];
	onComplete: () => void;
}

export default function NeedsAnalysisFlow({
	filteredNeedsAnalysis,
	onComplete,
}: NeedsAnalysisFlowProps) {
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	// const [answers, setAnswers] = useState<Record<number, QuestionnaireStepOption | null>>({});
	const [finalResult, setFinalResult] = useState<QuestionnaireImportance | null>(null);
	const hasTrackedStart = useRef(false);

	// Track needs analysis start on mount
	useEffect(() => {
		if (!hasTrackedStart.current && filteredNeedsAnalysis?.length > 0) {
			trackNeedsStart(filteredNeedsAnalysis.length);
			hasTrackedStart.current = true;
		}
	}, [filteredNeedsAnalysis?.length]);

	// Track step changes
	useEffect(() => {
		if (filteredNeedsAnalysis?.length > 0) {
			trackNeedsStep(currentStepIndex + 1, filteredNeedsAnalysis.length);
		}
	}, [currentStepIndex, filteredNeedsAnalysis?.length]);

	// Track completion when final result is set
	useEffect(() => {
		if (finalResult) {
			trackNeedsComplete(finalResult);
		}
	}, [finalResult]);

	const changeStepHandler = async (
		currentAnswer: QuestionnaireStepOption | null,
		toNext = true
	) => {
		if (toNext) {
			if (
				currentAnswer?.type === QuestionnaireImportance.IMPORTANT ||
				currentAnswer?.type === QuestionnaireImportance.STOP ||
				currentStepIndex === filteredNeedsAnalysis?.length - 1
			) {
				setFinalResult(currentAnswer?.type ?? null);
			}
		}

		setCurrentStepIndex((prev) =>
			toNext ? Math.min(prev + 1, filteredNeedsAnalysis.length - 1) : Math.max(prev - 1, 0)
		);
	};

	return (
		<div className="w-full h-full flex justify-center">
			{!finalResult && (
				<NeedsAnalysisSteps
					activeStep={currentStepIndex + 1}
					handleBack={(currentAnswer: QuestionnaireStepOption | null) => {
						changeStepHandler(currentAnswer, false);
					}}
					handleNext={(currentAnswer: QuestionnaireStepOption) => changeStepHandler(currentAnswer)}
					isLoading={false}
					stepInfo={filteredNeedsAnalysis?.[currentStepIndex]}
					totalStepLength={filteredNeedsAnalysis?.length ?? 0}
				/>
			)}
			{finalResult && (
				<NeedsAnalysisFinalResults
					isLoading={false}
					result={finalResult}
					onStartSelfAssessment={onComplete}
				/>
			)}
		</div>
	);
}
